import { Typography, useEventCallback } from '@mui/material';
import FilterMunicipalityList from 'civil-status/components/research-municipality/filter';
import TableMunicipality from 'civil-status/components/research-municipality/table';
import { ResearchMunicipalityCrumbs } from 'civil-status/constants/breadcrumb';
import {
  MunicipalityListVM,
  ResearchMunicipalityForm,
} from 'civil-status/view-models/research-municipality';
import { Form, Formik } from 'formik';
import { Fragment, ReactElement, useEffect, useState } from 'react';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import { __ACT_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { HttpResponseList } from 'shell/models/http-response';
import { Operation } from 'shell/view-models/operation';
import { useFilterTable } from 'shell/hooks/use-filter-table';
import { paginationListToQueryString } from 'system/utils/list-object-to-query-string';
import { OperationObject } from 'system/utils/operation-object-to-query-string';
import { useActionAuthorization } from 'shell/hooks/use-action-authorization';
import { CivilStatusAuthorizations } from 'shell/authorizations/civil-status';
import { transformerMunicipalityList } from 'civil-status/utils/transformer-municipality-list';
import { useNavigate } from 'react-router-dom';
import { ValueDomainType } from 'shell/models/value-domain-type';
import ListHeader from 'shell/components/shared/list-header';
// import * as yup from 'yup';

// const validationSchema = yup.object({
//   stateAct: yup.string().nullable().required('Campo obbligatorio'),
// });

export const ID_DOMINIO_COMUNI = 2;

const ResearchMunicipality = (): ReactElement => {
  const actions = useActionAuthorization({
    edit: [CivilStatusAuthorizations.AnscComuniUpdate],
    delete: [CivilStatusAuthorizations.AnscComuniDelete],
    insert: [CivilStatusAuthorizations.AnscComuniAdd],
  });

  const navigate = useNavigate();

  const [openFilter, setOpenFilter] = useState<boolean>(true);
  const [filterSelected, setFilterSelected] = useState<OperationObject>({});

  const filterTable = useFilterTable<ResearchMunicipalityForm>();

  const [tableProps, setTableProps] = useState<Record<string, number>>({
    page: filterTable?.values?.page || 0,
    rowsPerPage: filterTable?.values?.rowsPerPage || 10,
  });
  const [municipalityList, getMunicipalityList] = useHttpFetch<
    HttpResponseList<ValueDomainType>,
    Array<MunicipalityListVM>
  >(`${__ACT_API_URL__}/valori-dominio-atti`, {
    onBootstrap: false,
    options: {
      errorCatching: true,
      method: 'GET',
      loading: true,
      data: {},
    },
    transformer: transformerMunicipalityList,
  });

  const getMunicipalityListData = useEventCallback(
    (dataFilter: OperationObject, page?: number, rowsPerPage?: number) => {
      const queryParams = `${paginationListToQueryString({
        filters: dataFilter,
        orders: {},
        page: page || 0,
        rowsPerPage: rowsPerPage || 10,
      })}`;

      (
        getMunicipalityList({
          params: {},
          paramsSerializer: () => queryParams,
        }) as unknown as ResponsePromise<void>
      ).then(() => {
        // salvo i filtri dell'ultima ricerca
        setFilterSelected(dataFilter);
      });
    }
  );

  useEffect(() => {
    const dataFilter: OperationObject = {
      idDominio: {
        value: ID_DOMINIO_COMUNI,
        operation: Operation.EQUALS,
      },
      idValore: {
        value: filterTable?.values?.id ? Number.parseInt(filterTable?.values?.id, 10) : '',
        operation: Operation.EQUALS,
      },
      comune: {
        value: filterTable?.values?.description || '',
        operation: Operation.LIKE,
      },
    };

    getMunicipalityListData(
      dataFilter,
      filterTable?.values?.page || 0,
      filterTable?.values?.rowsPerPage || 10
    );
  }, []);

  const handleSubmit = useEventCallback((values: ResearchMunicipalityForm) => {
    const dataFilter: OperationObject = {
      idDominio: {
        value: ID_DOMINIO_COMUNI,
        operation: Operation.EQUALS,
      },
      idValore: {
        value: values?.id ? Number.parseInt(values?.id, 10) : '',
        operation: Operation.EQUALS,
      },
      comune: {
        value: values?.description || '',
        operation: Operation.LIKE,
      },
    };

    setTableProps({
      page: 0,
      rowsPerPage: 10,
    });

    filterTable.setValues({
      ...(filterTable?.values || {}),
      orders: filterTable?.values?.orders || {},
      page: 0,
      rowsPerPage: 10,
    });

    getMunicipalityListData(dataFilter);
  });

  return (
    <Fragment>
      <Breadcrumbs items={ResearchMunicipalityCrumbs} />
      <ListHeader
        addBtnAriaLabel="Aggiungi comune"
        addBtnLabel="Aggiungi comune"
        canAdd={actions.insert}
        onAddBtnClick={() => {
          navigate('/stato-civile/ricerca-comune/nuovo');
        }}
        title="Ricerca Comune"
      />
      <Formik
        initialValues={
          {
            id: '',
            description: '',
          } as ResearchMunicipalityForm
        }
        onSubmit={handleSubmit}
        validateOnMount
        // validationSchema={validationSchema}
      >
        <Form>
          <FilterMunicipalityList open={openFilter} setOpen={setOpenFilter} />
        </Form>
      </Formik>
      {municipalityList.data ? (
        ((municipalityList.data as Array<MunicipalityListVM>) || [])?.length > 0 ? (
          <>
            <TableMunicipality
              data={municipalityList.data as Array<MunicipalityListVM>}
              filterSelected={filterSelected}
              getMunicipalityListData={getMunicipalityListData}
              tableProps={tableProps}
              setTableProps={setTableProps}
              isAuthorizationEdit={actions.edit}
              isAuthorizationDelete={actions.delete}
            />
          </>
        ) : (
          <Typography color="secondary.main">
            Nessun elemento trovato con la ricerca effettuata.
          </Typography>
        )
      ) : undefined}
    </Fragment>
  );
};

ResearchMunicipality.displayName = 'Research Municipality Page component';

export default ResearchMunicipality;
